import { Check, Film, User } from 'lucide-react';

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w185';

interface MovieCardProps {
  type: 'movie';
  id: number;
  title: string;
  posterPath: string | null;
  releaseYear?: string;
  reason: string;
  isSelected: boolean;
  onToggle: () => void;
}

interface PersonCardProps {
  type: 'person';
  id: number;
  name: string;
  profilePath: string | null;
  reason: string;
  isSelected: boolean;
  onToggle: () => void;
}

type SuggestionCardProps = MovieCardProps | PersonCardProps;

export function SuggestionCard(props: SuggestionCardProps) {
  const { type, isSelected, onToggle, reason } = props;

  if (type === 'movie') {
    const { title, posterPath, releaseYear } = props as MovieCardProps;
    return (
      <button
        type="button"
        onClick={onToggle}
        className={`relative flex flex-col items-center p-2 rounded-xl border-2 transition-all ${
          isSelected
            ? 'border-cyan-500 bg-cyan-500/10'
            : 'ring-white/[0.08] bg-[#1A1F26]/50 hover:ring-white/[0.12]'
        }`}
      >
        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute top-1 right-1 w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center">
            <Check size={12} className="text-white" />
          </div>
        )}

        {/* Poster */}
        <div className="w-24 h-36 rounded-lg overflow-hidden bg-slate-700 flex-shrink-0 mb-2">
          {posterPath ? (
            <img
              src={`${TMDB_IMAGE_BASE}${posterPath}`}
              alt={title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Film size={28} className="text-slate-500" />
            </div>
          )}
        </div>

        {/* Title */}
        <p className="text-sm font-medium text-white text-center line-clamp-2 mb-1 min-h-[2.5rem]">
          {title}
        </p>

        {/* Year */}
        {releaseYear && (
          <p className="text-xs text-slate-400 mb-1">{releaseYear}</p>
        )}

        {/* Reason - full text visible */}
        <p
          className="text-xs text-cyan-400 text-center leading-relaxed px-1"
          title={reason}
        >
          {reason}
        </p>
      </button>
    );
  }

  // Person card
  const { name, profilePath } = props as PersonCardProps;
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`relative flex flex-col items-center p-2 rounded-xl border-2 transition-all ${
        isSelected
          ? 'border-cyan-500 bg-cyan-500/10'
          : 'ring-white/[0.08] bg-[#1A1F26]/50 hover:ring-white/[0.12]'
      }`}
    >
      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-1 right-1 w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center">
          <Check size={12} className="text-white" />
        </div>
      )}

      {/* Photo */}
      <div className="w-20 h-20 rounded-full overflow-hidden bg-slate-700 flex-shrink-0 mb-2">
        {profilePath ? (
          <img
            src={`${TMDB_IMAGE_BASE}${profilePath}`}
            alt={name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User size={28} className="text-slate-500" />
          </div>
        )}
      </div>

      {/* Name */}
      <p className="text-sm font-medium text-white text-center line-clamp-2 mb-1 min-h-[2.5rem]">
        {name}
      </p>

      {/* Reason - full text visible */}
      <p
        className="text-xs text-cyan-400 text-center leading-relaxed px-1"
        title={reason}
      >
        {reason}
      </p>
    </button>
  );
}
